import { wordpressApiHost } from './config'
import {
  getSessionItem,
  setSessionItem,
  removeSessionItem,
} from './storeManager'

import { getTimestamp } from './utils'
import { translateCategory } from './locale/localizationHelper'

const getCache = endpoint => {
  try {
    return JSON.parse(getSessionItem(endpoint))
  } catch {
    return null
  }
}

const setCache = (endpoint, data) => {
  setSessionItem(endpoint, JSON.stringify(data))
  setSessionItem(endpoint + '-timestamp', getTimestamp())
}

const removeCache = endpoint => {
  removeSessionItem(endpoint)
  removeSessionItem(endpoint + '-timestamp')
}

const isCacheOld = endpoint => {
  const limit = 10 * 60000 // Minutes * Milliseconds
  const timestamp =
    getTimestamp() - parseInt(getSessionItem(endpoint + '-timestamp'), 10)

  if (isNaN(timestamp)) return true
  return timestamp > limit
}

export const getEndPoint = (endpoint, { cache = true } = {}) => {
  let headers = { 'Content-Type': 'application/json; charset=UTF-8' }

  if (cache) {
    const cacheData = getCache(endpoint)
    if (cacheData === null || isCacheOld(endpoint)) {
      removeCache(endpoint)
    } else {
      return Promise.resolve(cacheData)
    }
  }
  return fetch(wordpressApiHost + endpoint, {
    headers: headers,
  }).then(res => {
    const { ok } = res

    if (ok) {
      return res.json().then(data => {
        if (cache) setCache(endpoint, data)
        return data
      })
    }

    return res.json().then(error => {
      throw error
    })
  })
}

export const getPagesByCategory = ({
  category,
  perPage = 100,
  page = 1,
  subCategory,
}) => {
  const endpoint = `wp/v2/${translateCategory(
    category
  )}?per_page=${perPage}&page=${page}&_embed`

  if (!subCategory) return getEndPoint(endpoint)

  return getEndPoint(
    endpoint +
      `&filter[meta_key]=category&filter[meta_compare]=LIKE&filter[meta_value]=${subCategory}`
  )
}

export const getPageBySlug = (category, slug) => {
  if (!category) return getOrphanPage(slug)
  // do the same transformation as in the fetchData function in wpFetch.js
  let pageFolder
  switch (category) {
    case 'posts':
      pageFolder = 'blog'
      break
    case 'marketing':
      pageFolder = 'news'
      break
    default:
      pageFolder = category
  }

  return import(
    /* webpackChunkName: "[request]" */ `../../wp-data/${pageFolder}/${slug}.json`
  )
    .catch(() =>
      getEndPoint(
        'wp/v2/' + translateCategory(category) + `?slug=${slug}&_embed`
      )
    )
    .then(json => {
      if (json.length === 0) return Promise.reject('Page not found')
      return json[0]
    })
}

// We don't know the category, so look through them all
// and look for the slug

export const getOrphanPage = slug =>
  import(/* webpackChunkName: "[request]" */ `../../wp-data/${slug}.json`)
    .catch(() => {
      const typesQueryString = [
        'fitness',
        'food',
        'health_education',
        'features',
        'marketing',
      ]
        .map(type => `type[]=${type}`)
        .join('&')

      return getEndPoint(`wp/v2/pages?${typesQueryString}&slug=${slug}&_embed`)
    })
    .then(pages => {
      if (pages.length === 0) {
        return Promise.reject('Page not found')
      }
      if (pages.length > 1) {
        pages = pages.sort((a, b) => b.acf.landing_page - a.acf.landing_page)
      }

      return Promise.resolve(pages[0])
    })
    .catch(error => Promise.reject(error))

export const getMedia = id => getEndPoint('wp/v2/media/' + id)

export const getPostTypes = () => {
  return getEndPoint('wp/v2/types').then(types => {
    let typesToKeep = {}

    if (types.features) {
      typesToKeep.features = types.features
    }

    if (types.fitness) {
      typesToKeep.fitness = types.fitness
    }

    if (types.food) {
      typesToKeep.food = types.food
    }

    if (types['health-education'] || types.health_education) {
      typesToKeep['health-education'] = types['health-education']
        ? types['health-education']
        : types.health_education
    }

    return typesToKeep
  })
}

export const getBlogCategories = () => getEndPoint('wp/v2/categories')

export const getBlogPostBySlug = slug => {
  return import(
    /* webpackChunkName: "[request]" */ `../../wp-data/blog/${slug}.json`
  )
    .catch(() => {
      return getEndPoint(`wp/v2/posts/?slug=${slug}&_embed`)
    })
    .then(json => {
      return json[0]
    })
}

export const getBlogPostsByPage = (page, offset) => {
  let endpoint = 'wp/v2/posts/?page=' + page
  if (offset) {
    endpoint += '&offset=' + offset
  }
  endpoint += '&_embed'
  return getEndPoint(endpoint)
}

export const getBlogCategoryPostsByPage = (category, page) => {
  let id
  return getBlogCategories().then(categories => {
    for (let blogCategory of categories) {
      if (blogCategory.slug === category) {
        id = blogCategory.id
      }
    }

    return getEndPoint(
      'wp/v2/posts/?categories=' + id + '&page=' + page + '&_embed'
    )
  })
}

export const getBlogAuthorPostsByPage = (author, page) => {
  let id
  return getAuthors().then(authors => {
    for (let blogAuthor of authors) {
      if (blogAuthor.slug === author) {
        id = blogAuthor.id
      }
    }

    return getEndPoint(
      'wp/v2/posts/?author=' + id + '&page=' + page + '&_embed'
    )
  })
}

export const getTags = () =>
  getEndPoint('wp/v2/tags?orderby=count&order=desc&per_page=20')

export const getBlogTagPostsByPage = (tag, page) => {
  return getTagBySlug(tag).then(tagData => {
    const id = tagData[0].id

    return getEndPoint('wp/v2/posts/?tags=' + id + '&page=' + page + '&_embed')
  })
}

export const getTagBySlug = slug => getEndPoint('wp/v2/tags?slug=' + slug)

export const getTagsByPostId = id => getEndPoint('wp/v2/tags?post=' + id)

export const getAuthors = () => getEndPoint('wp/v2/users')

// In listings, getAuthor can be called 10+ times before
// the data having had a chance to be fetched, so we do
// some special stuff to make sure we only get one author
// once.
let authorsBeingFetched = {}
export const resetAuthorsBeingFetchedWhenTesting = () => {
  // FIXME: this should be possible with jest.resetModules, but how?
  authorsBeingFetched = {}
}

export const getAuthor = id => {
  if (!authorsBeingFetched[id]) {
    authorsBeingFetched[id] = getEndPoint('wp/v2/users/' + id)
  }

  return authorsBeingFetched[id]
}

export const getAuthorBySlug = slug => getEndPoint('wp/v2/users/?slug=' + slug)

export const searchBlogPosts = term => getEndPoint('wp/v2/posts?search=' + term)

export const searchEducationArticles = term =>
  getEndPoint('wp/v2/health-education?search=' + term)

export const getCommentsByPostId = id =>
  getEndPoint('wp/v2/comments/?post=' + id)

export const getEducationArticles = () =>
  Promise.all([
    getEndPoint('wp/v2/health-education?page=1&per_page=100'),
    getEndPoint('wp/v2/health-education?page=2&per_page=100'),
  ])
    .then(([page1, page2]) => {
      return [...page1, ...page2]
    })
    .catch(() => [])

export const getEducationArticlesByCategory = category =>
  getEducationArticles().then(articles =>
    articles.filter(article => article.acf.category === category)
  )

export const getMiscData = () =>
  import(
    /* webpackChunkName: "[request]" */ '../../wp-data/miscellaneous.json'
  ).then(({ default: data }) => data[0])

export function getTermsFromPost(post) {
  if (!post._embedded) return { category: '', tags: [] }
  const { category: categories, post_tag: tags } = post._embedded[
    'wp:term'
  ].reduce((acc, current) => {
    if (current[0]) return { ...acc, [current[0].taxonomy]: current }
    return acc
  }, {})

  return { category: categories[0], tags }
}

export function getAuthorFromPost(post) {
  if (!post._embedded) return null
  const { author } = post._embedded
  return author && author[0]
}

export function getImageFromPost(post) {
  if (!post._embedded) return null
  const { 'wp:featuredmedia': featuredMedia } = post._embedded
  return featuredMedia && featuredMedia[0]
}
